import React from 'react';
import { AgentHistoryItem } from '../../types/dashboard';
import { 
  Calendar, 
  FileText, 
  Image as ImageIcon, 
  Type, 
  Sparkles,
  Clock,
  X,
} from 'lucide-react';

interface AgentHistoryModalProps {
  item: AgentHistoryItem | null;
  isOpen: boolean;
  onClose: () => void;
}

const AgentHistoryModal = ({ item, isOpen, onClose }: AgentHistoryModalProps) => {
  
  if (!isOpen || !item) return null;

  const formatDate = (dateString: string) => {
    const date = new Date(dateString);
    return date.toLocaleDateString('ko-KR', {
      year: 'numeric',
      month: 'long',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  const getAgentKindLabel = (kind: string) => {
    switch (kind) {
      case 'text-modal':
        return '텍스트 기반';
      case 'image-modal':
        return '이미지 기반';
      case 'gen-image-modal':
        return '이미지 생성';
      default:
        return kind;
    }
  };

  const getAgentKindIcon = (kind: string) => {
    switch (kind) {
      case 'text-modal':
        return <Type className="w-5 h-5" />;
      case 'image-modal':
        return <ImageIcon className="w-5 h-5" />;
      case 'gen-image-modal':
        return <Sparkles className="w-5 h-5" />;
      default:
        return <FileText className="w-5 h-5" />;
    }
  };

  const renderInputValue = (key: string, value: any) => {
    if (Array.isArray(value)) {
      return (
        <div className="flex flex-wrap gap-2">
          {value.map((item, index) => (
            <span key={index} className="px-2 py-1 bg-purple-500/20 text-purple-300 rounded-full text-sm">
              {item}
            </span>
          ))}
        </div>
      );
    }
    
    // 이미지 객체인 경우 처리
    if (typeof value === 'object' && value !== null) {
      if (value.data && value.ext) {
        return (
          <div className="space-y-2">
            <div className="flex items-center gap-2 text-sm text-slate-400">
              <ImageIcon className="w-4 h-4" />
              <span>업로드된 이미지 ({value.ext.toUpperCase()})</span>
            </div>
            <div className="bg-slate-800 rounded-lg p-4">
              <img
                src={`data:image/${value.ext};base64,${value.data}`}
                alt="업로드된 이미지"
                className="max-w-xs max-h-48 rounded-lg shadow-sm object-contain"
              />
            </div>
          </div>
        );
      }
      
      return (
        <div className="bg-slate-800 rounded-lg p-3">
          <pre className="text-sm text-slate-300 whitespace-pre-wrap">
            {JSON.stringify(value, null, 2)}
          </pre>
        </div>
      );
    }
    
    if (typeof value === 'string') {
      return <span className="text-slate-200">{value}</span>;
    }
    
    return <span className="text-slate-200">{String(value)}</span>;
  };

  const getQuestionFromSchema = (key: string) => {
    const property = item.agent_schema.properties[key];
    return property?.description || key;
  };

  return (
    <div className="fixed inset-0 z-50 overflow-y-auto">
      <div 
        className="fixed inset-0 bg-black bg-opacity-75 transition-opacity"
        onClick={onClose}
      />
      
      <div className="flex min-h-full items-center justify-center p-4">
        <div className="relative bg-slate-900 border border-slate-800 rounded-xl shadow-2xl max-w-4xl w-full max-h-[90vh] overflow-hidden">
          <div className="sticky top-0 bg-slate-900 border-b border-slate-800 p-6 flex items-center justify-between">
            <div className="flex items-center gap-2 md:gap-4">
              <img
                src={item.agent_image}
                alt={item.agent_title}
                className="w-10 h-10 md:w-12 md:h-12 rounded-lg object-cover border border-slate-700"
              />
              <div className="flex-1 min-w-0">
                <h2 className="text-lg md:text-xl font-bold text-white tracking-tight overflow-hidden text-ellipsis whitespace-nowrap">{item.agent_title}</h2>
                <div className="flex items-center gap-2 mt-1 text-sm md:text-base">
                  <span className="text-purple-400">{getAgentKindIcon(item.agent_kind)}</span>
                  <span className="text-xs md:text-sm text-slate-400">{getAgentKindLabel(item.agent_kind)}</span>
                  {item.child_license && (
                    <span className="text-xs bg-purple-500/20 text-purple-300 px-2 py-1 rounded-full">
                      아동용
                    </span>
                  )}
                </div>
              </div>
            </div>
            <button
              onClick={onClose}
              className="p-2 text-slate-400 hover:text-red-400 hover:bg-slate-800 rounded-lg transition-colors"
            >
              <X className="w-5 h-5" />
            </button>
          </div>

          <div className="p-6 overflow-y-auto custom-scrollbar max-h-[calc(80vh-120px)]">
            <div className="mb-8">
              <h3 className="text-base md:text-lg font-semibold mb-4 flex items-center gap-2 text-white">
                <FileText className="w-5 h-5 text-purple-400" />
                기본 정보
              </h3>
              <div className="bg-slate-800 border border-slate-700 rounded-xl p-4 space-y-3">
                <p className="text-slate-300 text-sm md:text-base">{item.agent_description}</p>
                <div className="flex items-center gap-4 text-sm text-slate-400">
                  <div className="flex items-center gap-2">
                    <Calendar className="w-4 h-4" />
                    <span>생성일: {formatDate(item.created_at)}</span>
                  </div>
                </div>
              </div>
            </div>

            <div className="mb-8">
              <h3 className="text-base md:text-lg font-semibold mb-4 flex items-center gap-2 text-white">
                <Clock className="w-5 h-5 text-purple-400" />
                입력한 답변
              </h3>
              <div className="space-y-4">
                {Object.entries(item.input_values).map(([key, value]) => (
                  <div key={key} className="bg-slate-800 border border-slate-700 rounded-xl p-4">
                    <h4 className="font-medium text-slate-200 mb-2 text-sm md:text-base">
                      {getQuestionFromSchema(key)}
                    </h4>
                    {renderInputValue(key, value)}
                  </div>
                ))}
              </div>
            </div>

            <div className="mb-6">
              <h3 className="text-base md:text-lg font-semibold mb-4 flex items-center gap-2 text-white">
                <Sparkles className="w-5 h-5 text-purple-400" />
                결과
              </h3>
              
              {/* 이미지 결과 */}
              {item.binary_values && item.binary_values.startsWith('http') && (
                <div className="mb-6">
                  <h4 className="font-medium text-slate-200 mb-3 text-sm md:text-base">생성된 이미지</h4>
                  <div className="bg-slate-800 border border-slate-700 rounded-xl p-4">
                    <img
                      src={item.binary_values}
                      alt="생성된 결과 이미지"
                      className="w-full max-w-md mx-auto rounded-lg shadow-md"
                    />
                  </div>
                </div>
              )}

              {/* 텍스트 결과 */}
              {item.output_values && (
                <div>
                  <h4 className="font-medium text-slate-200 mb-3 text-sm md:text-base">분석 결과</h4>
                  <div className="bg-slate-800 border border-slate-700 rounded-xl p-4">
                    <div className="prose prose-sm max-w-none">
                      {item.output_values.split('\n').map((line, index) => {
                        if (line.trim() === '') return <br key={index} />;
                        
                        if (line.startsWith('###')) {
                          return (
                            <h3 key={index} className="text-base md:text-lg font-semibold text-white mt-4 mb-2">
                              {line.replace('###', '').trim()}
                            </h3>
                          );
                        }
                        if (line.startsWith('**') && line.endsWith('**')) {
                          return (
                            <h4 key={index} className="font-semibold text-slate-200 mt-3 mb-1 text-sm md:text-base">
                              {line.replace(/\*\*/g, '')}
                            </h4>
                          );
                        }
                        
                        return (
                          <p key={index} className="text-slate-300 mb-2 leading-relaxed text-sm md:text-base">
                            {line}
                          </p>
                        );
                      })}
                    </div>
                  </div>
                </div>
              )}
            </div>
          </div>

          <div className="sticky bottom-0 bg-slate-900 border-t border-slate-800 p-6">
            <div className="flex justify-end">
              <button
                onClick={onClose}
                className="px-6 py-2 bg-slate-700 text-slate-200 rounded-lg hover:bg-slate-600 transition-colors border border-slate-600"
              >
                닫기
              </button>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default AgentHistoryModal; 